import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../theme';
import { getUnits } from '../utils/units';

const CATEGORY_COLORS = {
  'B-C4': '#ffe066',
  'B-C3': '#ffa020',
  'B-C2': '#e05000',
  'B-C1': '#c01010',
  'B-HC': '#7a0000',
};

const DESCENT_COLORS = {
  'B-D4': '#a0d0ff',
  'B-D3': '#4090e0',
  'B-D2': '#1060b0',
  'B-D1': '#003880',
  'B-DHC': '#001840',
};

const formatGradient = (v) => (v != null ? `${v.toFixed(1)}%` : '-');
const formatSpeed = (v, units) => v != null ? (units === 'imperial' ? `${(v * 0.621371).toFixed(1)} mph` : `${v.toFixed(1)} km/h`) : '-';
const formatPower = (v) => (v != null ? `${Math.round(v)} W` : '-');
const formatVam = (v) => (v != null ? `${Math.round(v)} m/h` : '-');
const formatElevation = (v) => (v != null ? `${Math.round(v)} m` : '-');
const fmtDistKm = (v, units) => v != null ? (units === 'imperial' ? (v * 0.621371).toFixed(2) : v.toFixed(2)) : '-';

const formatDuration = (secs) => {
  if (secs == null) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const ClimbChip = ({ value, colorMap }) => (
  <Chip
    label={value}
    size="small"
    sx={{
      backgroundColor: colorMap[value] || '#555',
      color: value?.endsWith('C4') || value?.endsWith('D4') ? '#1a1a1a' : '#fff',
      fontWeight: 'bold',
      fontSize: '0.7rem',
    }}
  />
);

const gridSx = (colors) => ({
  '& .MuiDataGrid-root': { border: 'none' },
  '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[700]}`, color: colors.grey[100] },
  '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.grey[700], color: colors.grey[100], borderBottom: 'none' },
  '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.grey[800] },
  '& .MuiDataGrid-footerContainer': { backgroundColor: colors.grey[700], color: colors.grey[100], borderTop: 'none' },
  '& .MuiDataGrid-row.row-highlighted': { backgroundColor: `${colors.grey[600]} !important` },
  '& .MuiDataGrid-row:hover': { backgroundColor: `${colors.grey[700]} !important` },
});


export const BikeClimbsGrid = ({ climbs = [], onClimbClick, onClimbDoubleClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [highlightedId, setHighlightedId] = useState(null);
  const [units, setUnits] = useState(getUnits());

  useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  if (!climbs || climbs.length === 0) {
    return (
      <Box sx={{ p: 3, backgroundColor: colors.grey[800], borderRadius: '8px', textAlign: 'center' }}>
        <Typography color={colors.grey[400]}>No climbs detected in this activity.</Typography>
      </Box>
    );
  }

  const rows = climbs.map((c, idx) => ({
    id: idx,
    category:         c.category,
    start_km:         c.start_distance_m / 1000,
    end_km:           c.end_distance_m / 1000,
    length_km:        c.length_m / 1000,
    elevation_gain_m: c.elevation_gain_m,
    avg_gradient_pct: c.avg_gradient_pct,
    max_gradient_pct: c.max_gradient_pct,
    duration_s:       c.duration_s,
    stopped_time_s:   c.stopped_time_s,
    avg_speed:        c.avg_speed,
    avg_power:        c.avg_power,
    vam:              c.vam,
  }));

  const hasPower = climbs.some(c => c.avg_power != null);
  const hasVam   = climbs.some(c => c.vam != null);

  const columns = [
    {
      field: 'category', headerName: 'Cat.', width: 80,
      renderCell: (p) => <ClimbChip value={p.value} colorMap={CATEGORY_COLORS} />,
    },
    {
      field: 'start_km', headerName: units === 'imperial' ? 'Start (mi)' : 'Start (km)', width: 95,
      valueFormatter: (v) => fmtDistKm(v, units),
    },
    {
      field: 'end_km', headerName: units === 'imperial' ? 'End (mi)' : 'End (km)', width: 90,
      valueFormatter: (v) => fmtDistKm(v, units),
    },
    {
      field: 'length_km', headerName: units === 'imperial' ? 'Length (mi)' : 'Length (km)', width: 105,
      valueFormatter: (v) => fmtDistKm(v, units),
    },
    {
      field: 'elevation_gain_m', headerName: '↑ Gain', width: 85,
      valueFormatter: (v) => formatElevation(v),
    },
    {
      field: 'avg_gradient_pct', headerName: 'Avg %', width: 80,
      valueFormatter: (v) => formatGradient(v),
    },
    {
      field: 'max_gradient_pct', headerName: 'Max %', width: 80,
      valueFormatter: (v) => formatGradient(v),
    },
    {
      field: 'duration_s', headerName: 'Duration', width: 95,
      valueFormatter: (v) => formatDuration(v),
    },
    {
      field: 'stopped_time_s', headerName: 'Stopped', width: 90,
      valueFormatter: (v) => formatDuration(v),
    },
    {
      field: 'avg_speed', headerName: 'Avg Speed', width: 100,
      valueFormatter: (v) => formatSpeed(v, units),
    },
    ...(hasPower ? [{
      field: 'avg_power', headerName: 'Avg Power', width: 100,
      valueFormatter: (v) => formatPower(v),
    }] : []),
    ...(hasVam ? [{
      field: 'vam', headerName: 'VAM', width: 90,
      valueFormatter: (v) => formatVam(v),
    }] : []),
  ];

  const gridHeight = Math.min(108 + rows.length * 52, 500);

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={1}>
        Detected Climbs
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
        Click to highlight on map and chart. Double-click to zoom.
      </Typography>
      <Box sx={{ height: gridHeight, ...gridSx(colors) }}>
        <DataGrid
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          getRowClassName={(p) => p.row.id === highlightedId ? 'row-highlighted' : ''}
          onRowClick={(p) => {
            const climb = climbs[p.row.id];
            const payload = { leftKm: climb.start_distance_m / 1000, rightKm: climb.end_distance_m / 1000, color: CATEGORY_COLORS[climb.category] || '#e05000' };
            if (onClimbClick) onClimbClick(payload);
          }}
          onRowDoubleClick={onClimbDoubleClick ? (p) => {
            const climb = climbs[p.row.id];
            setHighlightedId(p.row.id);
            onClimbDoubleClick({ leftKm: climb.start_distance_m / 1000, rightKm: climb.end_distance_m / 1000, color: CATEGORY_COLORS[climb.category] || '#e05000' });
          } : undefined}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
    </Box>
  );
};


export const BikeDescentGrid = ({ descents = [], onDescentClick, onDescentDoubleClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [highlightedId, setHighlightedId] = useState(null);
  const [units, setUnits] = useState(getUnits());

  useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  if (!descents || descents.length === 0) {
    return (
      <Box sx={{ p: 3, backgroundColor: colors.grey[800], borderRadius: '8px', textAlign: 'center' }}>
        <Typography color={colors.grey[400]}>No descents detected in this activity.</Typography>
      </Box>
    );
  }

  const rows = descents.map((d, idx) => ({
    id: idx,
    category:          d.category,
    start_km:          d.start_distance_m / 1000,
    end_km:            d.end_distance_m / 1000,
    length_km:         d.length_m / 1000,
    elevation_loss_m:  d.elevation_loss_m,
    avg_gradient_pct:  d.avg_gradient_pct,
    max_gradient_pct:  d.max_gradient_pct,
    duration_s:        d.duration_s,
    stopped_time_s:    d.stopped_time_s,
    avg_speed:         d.avg_speed,
    avg_power:         d.avg_power,
  }));

  const hasPower = descents.some(d => d.avg_power != null);

  const columns = [
    {
      field: 'category', headerName: 'Cat.', width: 80,
      renderCell: (p) => <ClimbChip value={p.value} colorMap={DESCENT_COLORS} />,
    },
    {
      field: 'start_km', headerName: units === 'imperial' ? 'Start (mi)' : 'Start (km)', width: 95,
      valueFormatter: (v) => fmtDistKm(v, units),
    },
    {
      field: 'end_km', headerName: units === 'imperial' ? 'End (mi)' : 'End (km)', width: 90,
      valueFormatter: (v) => fmtDistKm(v, units),
    },
    {
      field: 'length_km', headerName: units === 'imperial' ? 'Length (mi)' : 'Length (km)', width: 105,
      valueFormatter: (v) => fmtDistKm(v, units),
    },
    {
      field: 'elevation_loss_m', headerName: '↓ Loss', width: 85,
      valueFormatter: (v) => formatElevation(v),
    },
    {
      field: 'avg_gradient_pct', headerName: 'Avg %', width: 80,
      valueFormatter: (v) => formatGradient(v),
    },
    {
      field: 'max_gradient_pct', headerName: 'Max %', width: 80,
      valueFormatter: (v) => formatGradient(v),
    },
    {
      field: 'duration_s', headerName: 'Duration', width: 95,
      valueFormatter: (v) => formatDuration(v),
    },
    {
      field: 'stopped_time_s', headerName: 'Stopped', width: 90,
      valueFormatter: (v) => formatDuration(v),
    },
    {
      field: 'avg_speed', headerName: 'Avg Speed', width: 100,
      valueFormatter: (v) => formatSpeed(v, units),
    },
    ...(hasPower ? [{
      field: 'avg_power', headerName: 'Avg Power', width: 100,
      valueFormatter: (v) => formatPower(v),
    }] : []),
  ];

  const gridHeight = Math.min(108 + rows.length * 52, 500);

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={1}>
        Detected Descents
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
        Click to highlight on map and chart. Double-click to zoom.
      </Typography>
      <Box sx={{ height: gridHeight, ...gridSx(colors) }}>
        <DataGrid
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          getRowClassName={(p) => p.row.id === highlightedId ? 'row-highlighted' : ''}
          onRowClick={(p) => {
            const descent = descents[p.row.id];
            const payload = { leftKm: descent.start_distance_m / 1000, rightKm: descent.end_distance_m / 1000, color: DESCENT_COLORS[descent.category] || '#4090e0' };
            if (onDescentClick) onDescentClick(payload);
          }}
          onRowDoubleClick={onDescentDoubleClick ? (p) => {
            const descent = descents[p.row.id];
            setHighlightedId(p.row.id);
            onDescentDoubleClick({ leftKm: descent.start_distance_m / 1000, rightKm: descent.end_distance_m / 1000, color: DESCENT_COLORS[descent.category] || '#4090e0' });
          } : undefined}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
    </Box>
  );
};
